import { memoize } from "lodash";

import { reorderLanguages } from "app/components/campaigns/shared/CampaignsUtils";
import type { ISupportedLanguagesAndLocale } from "app/components/campaigns/campaigns-wizard/CampaignsWizardConsts";
import type { ICampaignsLanguage } from "app/components/campaigns/searchEngine/SearchEnginesConsts";
import { SynonymousCountriesNamesMap } from "app/components/campaigns/searchEngine/SearchEnginesConsts";

const ENGLISH_LANGUAGE = { description: "English", locale: "en-us" };

const handleLanguageWithCountry = (
    languageAndLocale: ISupportedLanguagesAndLocale,
    filteredLanguagesList: ISupportedLanguagesAndLocale[],
): ICampaignsLanguage => {
    const [country, language] = languageAndLocale.description.split(" - ");
    const shouldRemoveCountry =
        filteredLanguagesList.filter((item) => item.description.includes(language)).length === 1;
    const languageToPresent = shouldRemoveCountry ? language : `${language} - ${country}`;
    return { id: languageAndLocale.locale, text: languageToPresent };
};

const handleLanguageWithoutCountry = (languageAndLocale: ISupportedLanguagesAndLocale): ICampaignsLanguage => ({
    id: languageAndLocale.locale,
    text: languageAndLocale.description,
});

const addEnglishToLanguagesListIfMissing = (
    languages: ISupportedLanguagesAndLocale[] = [],
): ISupportedLanguagesAndLocale[] => {
    const listContainsEnglish = languages.some((language) =>
        language.description.includes(ENGLISH_LANGUAGE.description),
    );
    return listContainsEnglish ? languages : [...languages, ENGLISH_LANGUAGE];
};

const filterLanguageByCountry = (
    selectedCountry: string,
    languagesList: ISupportedLanguagesAndLocale[],
): ISupportedLanguagesAndLocale[] => {
    const countryNameToFilter = SynonymousCountriesNamesMap.get(selectedCountry) ?? selectedCountry;
    return languagesList.filter((language) => {
        const [country] = language.description.split(" - ");
        return country === countryNameToFilter.trim();
    });
};

const getSupportedLanguagesForCountryImpl = (
    country: string,
    languageList: ISupportedLanguagesAndLocale[],
): ICampaignsLanguage[] => {
    const selectedCountry = country.includes(",") ? country.split(",").pop().trim() : country.trim();

    const filteredLanguagesList = filterLanguageByCountry(selectedCountry, languageList);
    const languagesListWithEnglish = addEnglishToLanguagesListIfMissing(filteredLanguagesList);

    let res = languagesListWithEnglish.map((languageAndLocale) =>
        languageAndLocale.description.includes("-")
            ? handleLanguageWithCountry(languageAndLocale, filteredLanguagesList)
            : handleLanguageWithoutCountry(languageAndLocale),
    );

    res = reorderLanguages(res);
    return res;
};

// Memoize the getSupportedLanguagesForCountry function
export const getSupportedLanguagesForCountry = memoize(getSupportedLanguagesForCountryImpl);
